import React, { Suspense, useEffect, useRef, useState } from 'react'
import lazyWithRetry from '../../utils/lazyWithRetry'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'react-infinite-scroll-component'
import { studentAlerts } from '../../constants/StudentAnalyticsConstants'
import AlertsModal from '../modals/AlertsModal'
import AlertsNavigationButtons from './alerts/AlertsNavigationButtons'
import AssignFilter from './alerts/AssignFilter'
import { findAlerts } from '../../server/server'
import { usePageTitle, useQuery } from '../../utils/util'
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min'
import { useUser } from '../../auth'
import { alertsFilterList, assignmentFilterList } from './constants/AlertsConstants'
import { oneOfCodec, removeUrlParams, useUrlState } from '../../hooks/useUrlState'
import SkeletonAlertsHeader from './skeleton/SkeletonAlertsHeader'
import SkeletonAllAlerts from './skeleton/SkeletonAllAlerts'

const AlertsHeaderSection = lazyWithRetry(() => import('./AlertsHeaderSection'), './AlertsHeaderSection')
const AllAlerts = lazyWithRetry(() => import('./alerts/AllAlerts'), './alerts/AllAlerts')

function AlertsHome() {
    const { t, locale } = useTranslation()
    const query = useQuery()
    const { user } = useUser()
    const alertId = query.get('id')
    const [selContent, setSelContent] = useUrlState('tab', 'all', oneOfCodec(alertsFilterList))
    const [loading, setLoading] = useState(true)
    const [data, setData] = useState(studentAlerts)
    const [modalIsOpen, setModalIsOpen] = useState(false)
    const [resultNum, setResultNum] = useState(0)
    const [hasMore, setHasMore] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [selectedFilter, setSelectedFilter] = useUrlState('filter', assignmentFilterList[0], oneOfCodec(assignmentFilterList))
    const currentRequestIdRef = useRef(0)
    const history = useHistory()

    useEffect(() => {
        if (!user.alert_terms)
            setTimeout(() => {
                openAlertModal()
            }, 200)
    }, [])

    useEffect(() => {
        loadData({ loadedData: resultNum, typeFilter: selContent, assignedFilter: selectedFilter })
    }, [resultNum])

    const reloadData = () => {
        if (resultNum === 0) loadData({ loadedData: resultNum, typeFilter: selContent, assignedFilter: selectedFilter })
        else setResultNum(0)
    }

    const loadData = async ({ loadedData = resultNum, typeFilter = selContent, assignedFilter = selectedFilter }) => {
        if (resultNum === 0) {
            setLoading(true)
        } else {
            setLoadingMore(true)
        }
        const requestId = Date.now()
        currentRequestIdRef.current = requestId
        const resp = await findAlerts({ typeFilter, assignedFilter, loadedData: loadedData })
        if (requestId !== currentRequestIdRef.current) {
            setLoadingMore(false)
            setLoading(false)
            return
        }
        if (resp.status === 200) {
            const total = resp?.data?.total
            const alerts = resp?.data?.alerts
            const totalAlerts = resultNum === 0 ? alerts : [...data, ...alerts]
            setData(totalAlerts)
            if (resultNum === 0) {
                setHasMore(total > alerts?.length)
            } else {
                setHasMore(total > data?.length + alerts?.length)
            }
            if (!!alertId) {
                const found = totalAlerts.find((alert) => alert.id === alertId)
                if (found) {
                    setTimeout(() => {
                        const alertElement = document.getElementById(`alert-${alertId}`)
                        if (alertElement) {
                            alertElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                            alertElement.classList.add('highlight-alert')
                            setTimeout(() => {
                                // Drop only the deep-link id; the tab and filter
                                // params must survive.
                                removeUrlParams(history, 'id')
                                alertElement.classList.remove('highlight-alert')
                            }, 2000)
                        }
                    }, 300)
                } else {
                    if (resultNum === 0 && total > alerts?.length) {
                        setResultNum(totalAlerts?.length)
                    } else if (resultNum !== 0 && total > data?.length + alerts?.length) {
                        setResultNum(totalAlerts?.length)
                    } else {
                        // Alert isn't in this filter's results; give up on the
                        // deep link but keep the rest of the query string.
                        removeUrlParams(history, 'id')
                    }
                }
            }
            setLoadingMore(false)
            setLoading(false)
        } else {
            setData([])
            setLoading(false)
            setLoadingMore(false)
        }
    }

    const closeAlertModal = () => {
        setModalIsOpen(false)
    }
    const openAlertModal = () => {
        setModalIsOpen(true)
    }

    const selectFilter = (filter) => {
        setSelContent(filter)
        if (resultNum === 0) loadData({ loadedData: resultNum, typeFilter: filter, assignedFilter: selectedFilter })
        else {
            setResultNum(0)
        }
    }

    const selectAssignedFilter = (filter) => {
        setSelectedFilter(filter)
        if (resultNum === 0) loadData({ loadedData: resultNum, typeFilter: selContent, assignedFilter: filter })
        else setResultNum(0)
    }

    usePageTitle(`Upstrive Education - Alerts`)
    return (
        <div id="alerts-scroll" className="analytics-container light-small-scroll">
            <InfiniteScroll
                dataLength={data.length}
                next={() => {
                    if (!loading && !loadingMore) {
                        setResultNum(data?.length)
                    }
                }}
                hasMore={hasMore}
                scrollableTarget="alerts-scroll"
                className="analytics-inner-container mb-[40px]"
                scrollThreshold={0.8}
            >
                <div className="w-full bg-white rounded-[10px] flex flex-col">
                    <Suspense fallback={<SkeletonAlertsHeader />}>
                        <AlertsHeaderSection selContent={selContent} selectFilter={selectFilter} selectedFilter={selectedFilter} selectAssignedFilter={selectAssignedFilter} />
                    </Suspense>
                </div>
                <div className="w-full mt-5 flex-1">
                    <Suspense fallback={<SkeletonAllAlerts />}>
                        <AllAlerts data={data} loading={loading} loadData={reloadData} />
                    </Suspense>
                </div>
            </InfiniteScroll>
            <AlertsModal modalIsOpen={modalIsOpen} closeModal={closeAlertModal} />
        </div>
    )
}

export default AlertsHome
